import type { SessionUser } from '@salary/contracts'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ApiError, api, tokenStore } from './api'

interface Session {
  user?: SessionUser
  /** Still working out whether the stored token is any good. */
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  isAdmin: boolean
}

const SessionContext = createContext<Session | undefined>(undefined)

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser>()
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  /*
   * A stored token is a claim, not a session.
   *
   * It may have expired while the tab was closed, or been signed with a secret
   * the server has since been redeployed with. Asking the API who we are is one
   * request and removes a whole class of bug where the interface renders as
   * signed in and then every panel on it fails.
   */
  useEffect(() => {
    if (!tokenStore.read()) {
      setLoading(false)
      return
    }

    let cancelled = false
    api
      .me()
      .then((response) => {
        if (!cancelled) setUser(response.user)
      })
      .catch(() => {
        tokenStore.clear()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { token, user: signedIn } = await api.signIn(email, password)
    tokenStore.write(token)
    setUser(signedIn)
  }, [])

  const signOut = useCallback(() => {
    tokenStore.clear()
    setUser(undefined)
    // Otherwise the next person to sign in sees the last one's cached roster.
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<Session>(
    () => ({ user, loading, signIn, signOut, isAdmin: user?.role === 'HR_ADMIN' }),
    [user, loading, signIn, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession must be used inside a SessionProvider')
  return session
}

/**
 * Signs out when the API says the session is gone.
 *
 * A token expires mid-session eventually, and without this every panel shows
 * its own "Sign in to continue" while the shell insists you are signed in.
 */
export function useExpiredSessionHandler(): (error: unknown) => void {
  const { signOut, user } = useSession()

  return useCallback(
    (error: unknown) => {
      if (user && error instanceof ApiError && error.isUnauthorized) signOut()
    },
    [signOut, user],
  )
}
